import type { Editor } from "@tiptap/vue-3"

import { uuidv7 } from "uuidv7"

import { GlobalComposable } from "~/classes/composable/GlobalComposable"
import { StaticConversation } from "~/classes/conversation/StaticConversation"
import { conversationTurns, PropertyId, PropertySchema, PropertyStatus } from "~/classes/entity"
import { InterfaceSelection } from "~/classes/entity/InterfaceSelection"
import { StaticFile } from "~/classes/file/StaticFile"
import { InterfacePanelState } from "~/classes/generated/interface"
import { sendConversationTurnRequest } from "~/classes/handlers/conversation/turn"
import { StaticSearch } from "~/classes/search/StaticSearch"

interface State {
  isEditing: boolean
  /**
   * Set to whichever conversation id is currently set up. This is separate from the
   * 'current' conversation in useConversations() since there could be a delay in
   * initializing things.
   */
  initializedConversationId?: string
  /**
   * This is refreshed every time the browser is refreshed, which lets us know that we should
   * start a "new" conversation, i.e. send a timestamp update
   */
  isFirstMessage: boolean
  /**
   * The scroll distance from the bottom of the agent conversation, updated in real time.
   * If the user is within some margin of distance to the bottom, we auto-follow the
   * conversation as new messages are added.
   */
  scrollBottom: number
  inputTurn: TiptapUserInputTurnJson
  /**
   * When a file is added to the input, we want to keep a reference to it. If we don't
   * have a reference (e.g. blank/loading) then we have to run a task to fetch the data
   * from our cache.
   */
  fileReferences: Record<string, StaticFile>
  /**
   * When a search is returned, we want to keep a reference to it.
   * TODO: should this go in useSearch?
   */
  searchReferences: Record<string, StaticSearch>
}

class Agent extends GlobalComposable<State> {
  editor: Ref<Editor | undefined> = ref(undefined)
  // const this = useLogger("use-agent", theme.colors.red.hex)

  override defaultState = (): State => {
    return {
      isEditing: false,
      isFirstMessage: true,
      scrollBottom: 0,
      inputTurn: this.defaultInputTurnContentJson(),
      fileReferences: {},
      searchReferences: {},
    }
  }

  override use = () => {
    this.state = useState("use-agent", this.defaultState)
    this.editor = useState("use-agent-editor", () => undefined)
    return this
  }

  override onCreate = () => {
    const { addInterfaceEventListener } = useEvents()
    addInterfaceEventListener("focusOnAgentInput", this.focusOnInputTurn)
    addInterfaceEventListener("afterNavigatedToConversation", this.onSwitchedToConversation)
  }

  override onDestroy = () => {
    const { removeInterfaceEventListener } = useEvents()
    removeInterfaceEventListener("focusOnAgentInput", this.focusOnInputTurn)
    removeInterfaceEventListener("afterNavigatedToConversation", this.onSwitchedToConversation)
  }

  onSwitchedToConversation = (conversation: StaticConversation) => {
    const { isAuthenticated, persistUserInterfaceSettings } = useSelf()

    if (conversation.id === this.state.value.initializedConversationId)
      return this.debug("Skipping already initialized conversation", conversation)

    if (this.state.value.initializedConversationId)
      this.deinitializeListeners()
    if (this.editor.value)
      this.clearUserInput()
    this.clearState()
    this.initialize(conversation)

    if (isAuthenticated.value)
      persistUserInterfaceSettings({ latestConversationId: conversation.id })

    // don't need to await on this, just kick off retrieving properties.
    conversation.getProperties()
  }

  /**
   * Initialize a conversation by adding our file listeners and other reactive handlers
   */
  initialize = (conversation: StaticConversation) => {
    if (this.state.value.initializedConversationId) {
      if (this.state.value.initializedConversationId === conversation.id)
        return this.warn("Already initialized conversation", conversation.id)
      else {
        throw new Error("Conversation was not deinitialized")
      }
    }
    // now we have to initialize all the file references and attach event listeners to the files so that they update when the files themselves get updated
    this.initializeListeners(conversation.id)
    this.state.value.initializedConversationId = conversation.id
  }

  isEditorEmpty = () => {
    if (!this.editor.value)
      throw new Error("No editor instance")
    return this.editor.value.$doc.textContent === ""
  }

  getCurrentEditorFragmentText = () => {
    if (!this.editor.value)
      throw new Error("No editor instance")
    return this.editor.value.state.selection.$from.node().textContent
  }

  defaultInputTurnContentJson = (): TiptapUserInputTurnJson => {
    return { type: "doc", content: [{ type: "paragraph", content: [] }] }
  }

  /** Tiptap editor keydown observer, returns true if we want to prevent bubbling */
  onKeyDown = (editor: Editor, event: KeyboardEvent) => {
    const { isOffline } = useCache()

    if (event.key === "Enter" && this.shiftKeyModifier(event)) {
      console.debug("Inserting new line in editor (Shift+Enter)")

      if (!this.editor.value)
        throw new Error("No editor")

      this.state.value.inputTurn.content.push({ type: "paragraph", content: [] })
      this.refreshEditorView()
      return true
    }
    else if (event.key === "Enter") {
      if (isOffline()) {
        this.error("Cannot send message while offline")
        return true
      }

      const input = this.parseEditorContent(editor.getJSON() as any)
      const isEmpty = all(input.fragments, (fragment) => {
        if ("text" in fragment)
          return fragment.text.trim().length === 0
        return false
      })
      if (isEmpty)
        this.error("Input is empty, skipping", input)
      else {
        this.sendUserInputTurn(input)
        // scroll to bottom after message is sent.
        this.scrollToBottomIfFollowing()
      }

      return true
    }
    return false
  }

  protected commandKeyModifier = (keyboard: KeyboardEvent) => keyboard.metaKey || keyboard.ctrlKey
  protected shiftKeyModifier = (keyboard: KeyboardEvent) => keyboard.shiftKey

  onEditorUpdated = (editor: Editor) => {
  // Update our intent value to the current json
    this.state.value.inputTurn = editor.getJSON() as TiptapUserInputTurnJson

  // TODO: handle other updates
  }

  onConversationProperties = async (event: ConversationEvent) => {
    const conversation = StaticConversation.fromEvent(event)
    if (conversation.id !== this.state.value.initializedConversationId)
      return this.warn("Received properties for different conversation", conversation.id)

    // if we receive any file references or search references, add them to our
    // state. Or if we received a response that would end the conversation, end it.

    for (const property of conversation.turnProperties()) {
      if (property.value.type !== PropertySchema.ConversationTurnValue)
        continue
      const turn = property.value.turn
      if ("user" in turn && typeof turn.user !== "string" && "input" in turn.user) {
        for (const fragment of turn.user.input.fragments) {
          if (!("fileRef" in fragment))
            continue
          const fileId = fragment.fileRef.fileId
          this.addFileReference(StaticFile.blank(fileId))
        }
      }
      else if ("agent" in turn && "output" in turn.agent) {
        for (const fragment of turn.agent.output.fragments) {
          if (typeof fragment === "string")
            continue // skip the 'unused' fragment type
          if ("fileRef" in fragment) {
            const iFile = new StaticFile(fragment.fileRef.file)
            if (this.state.value.fileReferences[iFile.id])
              continue
            this.addFileReference(iFile)
          }
        }
      }
      else if ("agent" in turn && "status" in turn.agent) {
        for (const part of turn.agent.status.parts) {
          if ("fileLoadRequested" in part) {
            const iFile = new StaticFile(part.fileLoadRequested.request.file)
            this.addFileReference(iFile)
          }
          else if ("fileCreateRequested" in part) {
            const iFile = new StaticFile(part.fileCreateRequested.request.file)
            this.addFileReference(iFile)
          }
          else if ("fileUpdateRequested" in part) {
            const iFile = new StaticFile(part.fileUpdateRequested.request.file)
            this.addFileReference(iFile)
          }
          else if ("fileMoveRequested" in part) {
            const iFile = new StaticFile(part.fileMoveRequested.request.file)
            this.addFileReference(iFile)
          }
          else if ("fileEditRequested" in part) {
            const iFile = new StaticFile(part.fileEditRequested.request.file)
            this.addFileReference(iFile)
          }
          else if ("searchListRequested" in part) {
            const iSearch = new StaticSearch(part.searchListRequested.request.search)
            this.addSearchReference(iSearch)
          }
        }
      }
    }

    // once the vue state has been updated, we need to scroll to the bottom of the conversation
    // if we are already following the conversation
    this.scrollToBottomIfFollowing()
  }

  /** TODO: add a command around opening and focusing? */
  focusOnInputTurn = () => {
    if (!this.editor.value)
      return this.error("Cannot focus on editor: no editor instance")
    this.editor.value.commands.focus("end")
  }

  scrollToBottomIfFollowing = async () => {
    const { emitInterfaceEvent } = useEvents()
    if (!this.isFollowing.value)
      return console.warn("Not scrolling to bottom, user is not following the conversation")

    // this seems to be more reliable than just nextTick() at updating only after the state
    // change occurs. Otherwise we would call the update before the transcript is updated
    await sleep(1000 / 30)

    emitInterfaceEvent("scrollToLatestAgentMessage", {})
  }

  isFollowing = computed(() => this.state.value.scrollBottom <= 40)

  parseEditorContent = (editorContent: TiptapUserInputTurnJson): UserInputTurn => {
    const { state: myself } = useSelf()
    const { currentFilePreview, currentRootFolder } = useNavigation()
    const { state: sharedFolderState } = useSharedFolders()

    const { type: dType, content: dContent } = editorContent
    if (dType !== "doc")
      throw new Error("Editor JSON not a doc type")

    // TODO: remove default search locations!
    if (!myself.value.home || !myself.value.archive || !myself.value.user)
      throw new Error("Must be logged in")
    const locations: Set<string> = new Set()
    locations.add(myself.value.home.id)
    for (const { home } of Object.values(sharedFolderState.value.all))
      locations.add(home.id)

    // add your current location (either file or folder) as a file ref with the "current"
    // property (this is how we reference the "current location" to the agent)
    let current
    if (currentFilePreview.value || currentRootFolder.value) {
      const file = (currentFilePreview.value ?? currentRootFolder.value)!
      if (!isDefined(file.path())
        || !isDefined(file.mimeType())
      )
        console.error("File missing path, mime type or thumbnail set", file)

      // add this folder's root to the search locations
      if (isDefined(file.asStatic().rootFolder()))
        locations.add(file.asStatic().rootFolder()!.id)
      else
        console.error("Current file missing root folder", file)

      // we only want to add a few properties: name, media type, thumbnails, path
      current = StaticFile.blank(file.id)
      current.setPath(file.path()!)
      for (const property of [
        PropertyId.FILE_NAME,
        PropertyId.FILE_MIME_TYPE,
        PropertyId.THUMBNAIL_SET,
      ])
        if (isDefined(file.property(property)))
          current.addProperty(file.property(property)!)
    }

    const fragments: UserInputTurnFragment[] = []

    // if the conversation is not already in progress, add a "timestamp"

    // Now add what's in the actual editor content
    for (const paragraph of dContent) {
      if (paragraph.type !== "paragraph")
        throw new Error("Paragraph type not a paragraph")
      if (paragraph.content) {
        for (const fragment of paragraph.content) {
          if (fragment.type === "text") {
            if (fragment.text.trim().length !== 0)
              fragments.push({ text: fragment.text })
          }
          else if (fragment.type === "fileRef") {
            const { fileId } = fragment.attrs
            if (!fileId)
              throw new Error("File reference missing file id")
            const file = this.state.value.fileReferences[fileId]
            if (!isDefined(file) || !isDefined(file.mediaType()))
              throw new Error("File reference missing file or media type")
            fragments.push({ fileRef: { fileId, mediaType: file.mediaType()! } })
          }
        }
      }
      // at the end of each paragraph, add a newline
      fragments.push({ text: "\n" })
    }
    fragments.pop() // remove the last newline fragment to avoid trailing newlines

    return {
      fragments,
      searchLocations: list(locations),
      current: current?.inner(),
      userId: myself.value.user.id,
    }
  }

  sendUserInputTurn = (input: UserInputTurn) => {
    const { state: myself } = useSelf()

    this.debug("Sending user input turn", input)

    this.clearUserInput()

    if (!this.state.value.initializedConversationId)
      throw new Error("Cannot send user input without conversation")
    if (!myself.value.user)
      throw new Error("Must be logged in (for now)")

    const conversation = StaticConversation.blank(this.state.value.initializedConversationId)

    const turn: ConversationTurn = { user: { input } }
    const timestamp = uuidv7()
    const property: ConversationProperty = {
      id: this.state.value.initializedConversationId,
      propertyId: conversationTurns(timestamp),
      version: 0,
      schema: PropertySchema.ConversationTurnValue,
      value: { type: PropertySchema.ConversationTurnValue, turn },
      timestamp,
      status: PropertyStatus.StatusPending,
      author: myself.value.user.id,
    }

    conversation.addProperty(property)
    const request: ConversationTurnRequestDatagram = {
      requestId: generateWorkerRequestId(),
      offline: false,
      conversation: conversation.inner(),
    }
    sendConversationTurnRequest(request)

    return request
  }

  addTextToUserInput = (text: string) => {
    if (!this.editor.value)
      throw new Error("No editor")

    // add the item to the final paragraph in the text, and add a " " after it

    const fragments: TipTapUserParagraphContent[] = []
    fragments.push({ type: "text", text })

    const len = this.state.value.inputTurn.content.length
    this.state.value.inputTurn.content[len - 1].content ??= []
    this.state.value.inputTurn.content[len - 1].content!.push(...fragments)
    this.refreshEditorView()
  }

  addFilesToUserInput = (files: StaticFile[]) => {
    if (!this.editor.value)
      throw new Error("No editor")

    // add the item to the final paragraph in the text, and add a " " after it

    const fragments: TipTapUserParagraphContent[] = []
    for (const file of files) {
      this.state.value.fileReferences[file.id] = file
      this.addFileListeners(file.id)
      fragments.push({ type: "fileRef", attrs: { fileId: file.id } })
    }
    fragments.push({ type: "text", text: " " })

    const len = this.state.value.inputTurn.content.length
    this.state.value.inputTurn.content[len - 1].content ??= []
    this.state.value.inputTurn.content[len - 1].content!.push(...fragments)
    this.refreshEditorView()
  }

  refreshEditorView = () => {
    if (!this.editor.value)
      throw new Error("No editor value")
    return this.editor.value
      .chain()
      .setContent(this.state.value.inputTurn.content)
      .focus()
      .run()
  }

  clearUserInput = () => {
    if (!this.editor.value)
      throw new Error("No editor")
    this.state.value.inputTurn = this.defaultInputTurnContentJson()
    return this.refreshEditorView()
  }

  isInProgress = computed(() => {
    const { currentConversation } = useConversations()
    if (!currentConversation.value)
      return false
    if (currentConversation.value.isClosed())
      return false
    return true
  })

  cancelAgentTurn = async () => {
    const { currentConversation } = useConversations()
    if (!this.state.value.initializedConversationId)
      throw new Error("Cannot cancel agent turn without conversation initialized")
    if (!currentConversation.value)
      throw new Error("No current conversation to cancel turn in")
    if (!this.isInProgress.value)
      return this.warn("Cannot cancel, conversation is not in progress")
    try {
      await currentConversation.value.close()
    }
    catch (error) {
      this.error("Failed to cancel agent turn", error)
    }
  }

  askAgentQuery = async (query: string, existingChat = false) => {
    const { showPanelAndPersist, isShowingPanel } = usePanel()
    const { getLatestIfEmptyOrCreateConversation, setupCurrentConversationOrCreateNewOne } = useConversations()

    if (this.editor.value)
      this.clearUserInput()

    if (!existingChat) {
      const conversation = await getLatestIfEmptyOrCreateConversation()
      setupCurrentConversationOrCreateNewOne(conversation)
    }

    // if the panel isn't showing we need to show it first, and await for vue
    // to update the state
    if (!isShowingPanel(InterfacePanelState.Agent)) {
      showPanelAndPersist(InterfacePanelState.Agent)
      await nextTick()
    }

    // this will also focus on the input immediately
    this.addTextToUserInput(query)

    // now send the query
    const turn = this.parseEditorContent(this.editor.value!.getJSON() as TiptapUserInputTurnJson)
    this.sendUserInputTurn(turn)
  }

  /**
   * Asking the agent about a file means:
   * - clear agent input
   * - open agent side panel
   * - add the file to the agent input
   * - show the cursor to let the user complete their question
   */
  askAgentAboutFiles = async (files: Record<string, StaticFile>, existingChat = false) => {
    const { showPanelAndPersist, isShowingPanel } = usePanel()
    const { getLatestIfEmptyOrCreateConversation, setupCurrentConversationOrCreateNewOne } = useConversations()

    if (this.editor.value)
      this.clearUserInput()

    if (!existingChat) {
      const conversation = await getLatestIfEmptyOrCreateConversation()
      setupCurrentConversationOrCreateNewOne(conversation)
    }

    // if the panel isn't showing we need to show it first, and await for vue
    // to update the state
    if (!isShowingPanel(InterfacePanelState.Agent)) {
      showPanelAndPersist(InterfacePanelState.Agent)
      await nextTick()
    }

    // this will also focus on the input immediately
    this.addFilesToUserInput(Object.values(files))
  }

  askAgentAboutAFile = async (file: StaticFile, existingChat = false) => {
    return this.askAgentAboutFiles({ [file.id]: file }, existingChat)
  }

  requestConversationHistory = () => {
    throw new Error("Requesting history unimplemented")
  }

  initializeListeners = (conversationId: string) => {
    const { addEntityEventListener } = useEvents()
    const { staticConversation: conversations } = useConversations()

    addEntityEventListener(
      "conversationProperties",
      conversationId,
      this.onConversationProperties,
    )

    const conversation = conversations(conversationId)
    if (!conversation)
      return console.error(`No conversation with id ${conversationId} found`)

    const transcript = conversation.turns()
    for (const turn of transcript) {
      if ("user" in turn) {
        if (typeof turn.user === "string" || !("input" in turn.user))
          continue
        for (const fragment of turn.user.input.fragments) {
          if (!("fileRef" in fragment))
            continue
          const fileId = fragment.fileRef.fileId
          this.addFileReference(StaticFile.blank(fileId))
        }
      }
      else if ("agent" in turn) {
        if (!("output" in turn.agent))
          continue
        for (const fragment of turn.agent.output.fragments) {
          if (typeof fragment === "string")
            continue // skip the 'unused' fragment type
          if ("fileRef" in fragment) {
            const iFile = new StaticFile(fragment.fileRef.file)
            this.addFileReference(iFile)
          }
        }
      }
      else {
        this.warn("Unknown turn", turn)
        throw new Error("Unknown turn type")
      }
    }
  }

  deinitializeListeners = () => {
    const { removeEntityEventListener } = useEvents()
    if (!this.state.value.initializedConversationId)
      throw new Error("No conversation initialized, nothing to deinitialize")
    for (const fileId in this.state.value.fileReferences)
      this.removeFileListeners(fileId)

    removeEntityEventListener(
      "conversationProperties",
      this.state.value.initializedConversationId,
      this.onConversationProperties,
    )

    delete this.state.value.initializedConversationId
  }

  getFileReference = (fileId: string) => {
    const file = this.state.value.fileReferences[fileId]
    if (file && !file.isBlank())
      return file
  }

  /**
   * TODO: convert this to using reactive file refs
   */
  addFileReference = (file: StaticFile) => {
    const { getFileProperties } = useFileSystem()
    if (this.state.value.fileReferences[file.id])
      return
    this.state.value.fileReferences[file.id] = file
    this.addFileListeners(file.id)
    getFileProperties(file.id, InterfaceSelection.minimumTranscriptSelection().inner())
  }

  addFileListeners = (fileId: string) => {
    const { addEntityEventListener } = useEvents()
    addEntityEventListener("fileProperties", fileId, this.onTranscriptFileProperties)
    addEntityEventListener("fileDeleted", fileId, this.onTranscriptFileDeleted)
  }

  removeFileListeners = (fileId: string) => {
    const { removeEntityEventListener } = useEvents()
    removeEntityEventListener("fileProperties", fileId, this.onTranscriptFileProperties)
    removeEntityEventListener("fileDeleted", fileId, this.onTranscriptFileDeleted)
  }

  getSearchReference = (searchId: string) => {
    const search = this.state.value.searchReferences[searchId]

    if (search && !search.isBlank())
      return search
  }

  addSearchReference = (search: StaticSearch) => {
  // const { getSearchProperties } = useSearch()
    if (this.state.value.searchReferences[search.id])
      return
    this.state.value.searchReferences[search.id] = search

  // TODO: retrieve search from cache
  // getSearchProperties(searchId, StaticSearch.minimumTranscriptSelection())
  }

  onTranscriptFileProperties = (event: FileEvent) => {
    const file = StaticFile.fromEvent(event)
    if (this.state.value.fileReferences[file.id]) {
      const newFile = this.state.value.fileReferences[file.id]
      newFile.mergeWithNewer(file)
      this.state.value.fileReferences[file.id] = new StaticFile(newFile.inner())
    }
    else
      this.state.value.fileReferences[file.id] = file
  }

  onTranscriptFileDeleted = (_event: FileEvent) => {
    throw new Error("Function not implemented.")
  }
}

const agent = new Agent("agent", theme.colors.red.hex)
export default function () {
  return agent.use()
}
